import { Box, VStack } from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionFolderValidationErrorReviewFragment,
  IngestionPlatform,
} from '@client/graphql/__generated__/types';
import { FC } from 'react';
import { ComplianceValidationVariant } from '../ComplianceDrawer';
import { IssueFormData } from '../components/AddIssueModal';
import { DefaultDocumentLayout } from './DefaultDocumentLayout';
import { DocumentsSidebarHeader } from './DocumentsSidebarHeader';
import { FolderGroupedDocumentLayout } from './FolderGroupedDocumentLayout';

interface ComplianceDocumentsSidebarProps {
  variant: ComplianceValidationVariant;
  documents: ExternalIngestionDocumentFragment[];
  loading: boolean;

  // [NOTE] we pass down selectedIngestionDocumentId for quicker UI updates before selectedIngestionDocument is fetched
  selectedDocumentId: string | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  folder: ExternalIngestionFolderFragment;
  validationData: ExternalValidationReportDataFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  userAddedFolderIssues?: IngestionFolderValidationErrorReviewFragment[];
  hiddenFolderIssues?: IngestionFolderValidationErrorReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  width?: string;
  selectFirstDocument?: boolean;
  hideViewAllIssues?: boolean;
  onDocumentSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
  onViewAllIssues: () => void;
  onAddFolderIssue?: (issueData: IssueFormData) => void | Promise<void>;
  onDeleteFolderIssue?: (reviewId: string) => void | Promise<void>;
  onHideFolderIssue?: (
    isHidden: boolean,
    message?: string,
    documentCategory?: string,
    documentForm?: string,
    documentName?: string,
    ruleUuid?: string
  ) => void | Promise<void>;
}

export const ComplianceDocumentsSidebar: FC<
  ComplianceDocumentsSidebarProps
> = ({
  variant,
  documents,
  loading,
  selectedDocumentId,
  selectedDocument,
  folder,
  validationData,
  documentReviews,
  submittedDocumentReviews,
  userAddedFolderIssues,
  hiddenFolderIssues,
  hiddenDocumentValidationReviews,
  hideViewAllIssues = false,
  selectFirstDocument = true,
  onDocumentSelect,
  onViewAllIssues,
  onAddFolderIssue,
  onDeleteFolderIssue,
  onHideFolderIssue,
  width = '350px',
}) => {
  const getLayoutComponent = () => {
    // Use FolderGroupedDocumentLayout for DOTLOOP, SKYSLOPE and ZIPFORM platforms
    if (
      [
        IngestionPlatform.DOTLOOP,
        IngestionPlatform.SKYSLOPE,
        IngestionPlatform.ZIPFORM,
      ].includes(folder.platform)
    ) {
      return FolderGroupedDocumentLayout;
    }

    // Default layout for all other platforms
    return DefaultDocumentLayout;
  };

  const LayoutComponent = getLayoutComponent();

  return (
    <Box
      borderColor="compliance.sidebar.border"
      borderRightWidth={1}
      height="100%"
      overflow="auto"
      p={3}
      w={width}
    >
      <VStack align="stretch" spacing={4}>
        <DocumentsSidebarHeader
          folder={folder}
          hiddenFolderIssues={hiddenFolderIssues}
          hideViewAllIssues={hideViewAllIssues}
          loading={loading}
          userAddedFolderIssues={userAddedFolderIssues}
          validationData={validationData}
          variant={variant}
          onAddFolderIssue={onAddFolderIssue}
          onDeleteFolderIssue={onDeleteFolderIssue}
          onHideFolderIssue={onHideFolderIssue}
          onViewAllIssues={onViewAllIssues}
        />
        {documents?.length > 0 && (
          <LayoutComponent
            documentReviews={documentReviews}
            documents={documents}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            selectFirstDocument={selectFirstDocument}
            selectedDocument={selectedDocument}
            selectedDocumentId={selectedDocumentId}
            submittedDocumentReviews={submittedDocumentReviews}
            validationData={validationData}
            onDocumentSelect={onDocumentSelect}
          />
        )}
      </VStack>
    </Box>
  );
};
